import {
    ProductMapPrices, PricesType, SalesPeriod, PricePercent, PriceRoundingType, PriceDetail, ProductPricesDetail, UnitPrice,
} from '../models/product-prices.model';

import { ProductPublicModels } from '@features/products/product';

export function getPriceUseCase(
    productPrice: ProductPublicModels.PublicProductPrice,
    period: string,
    showPrivateSale: boolean
): PriceDetail {
    const price = new PriceDetail('price', productPrice);
    // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar el codigo legacy)
    const vipPrice = new PriceDetail('vipPrice', productPrice);
    const futurePrice = new PriceDetail('futurePrice', productPrice);
    const periodUser = determinePeriodUser(period, showPrivateSale);

    switch (periodUser) {
        case SalesPeriod.SALES:
        case SalesPeriod.VEA:
            // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar el codigo legacy)
            if (vipPrice.value && !vipPrice.isEqual(price.value)) {
                return vipPrice;
            }
            break;
        case SalesPeriod.PREWARM:
            if (futurePrice.value && !futurePrice.isEqual(price.value)) {
                return futurePrice;
            }
            break;
    }

    return price;
}

// OLDPRICE is a secondary price
export function getOldPriceUseCase(
    productPrice: ProductPublicModels.PublicProductPrice,
    primaryPrice: PriceDetail,
    showPrivateSale: boolean // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar el codigo legacy)
): PriceDetail {
    const price = new PriceDetail('price', productPrice);
    const oldPrice = new PriceDetail('oldPrice', productPrice);
    const hasFuturePrice = primaryPrice.hasType(PricesType.FUTUREPRICE);
    const hasValueAnIsDiferent = price.value && !price.isEqual(primaryPrice.value);

    // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar el codigo legacy)
    const oldVipPrice = new PriceDetail('oldVipPrice', productPrice);
    const hasVipPrice = primaryPrice.hasType(PricesType.VIPPRICE);

    // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar el codigo legacy)
    if (showPrivateSale && hasVipPrice && oldVipPrice.value) {
        return oldVipPrice;
    }

    // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar comprobación hasVipPrice del if)
    if ((hasVipPrice || hasFuturePrice) && hasValueAnIsDiferent) {
        return price;
    }

    if (oldPrice.value && !oldPrice.isEqual(primaryPrice.value)) {
        return oldPrice;
    }

    oldPrice.reset();
    return oldPrice;
}

// LastReducedPrice is a terciaryPrice
export function getLastReducedPriceUseCase(
    productPrice: ProductPublicModels.PublicProductPrice,
    secondary: PriceDetail,
    confEnableTriplePrice: boolean
): PriceDetail {
    const oldPrice = new PriceDetail('oldPrice', productPrice);
    if (confEnableTriplePrice
            && secondary.hasType(PricesType.PRICE)
            && (oldPrice.value && !oldPrice.isEqual(secondary.value))
    ) {
        return oldPrice;
    }
    oldPrice.reset();
    return oldPrice;
}

export function getOriginalPriceUseCase(
    productPrice: ProductPublicModels.PublicProductPrice,
    secondary: PriceDetail,
    confEnableTriplePrice: boolean
): PriceDetail {
    const originalPrice = new PriceDetail('originalPrice', productPrice);
    if (confEnableTriplePrice && !secondary.isEqual(originalPrice.value)) {
        return originalPrice;
    }
    originalPrice.reset();
    return originalPrice;
}

export function getUnitPriceUseCase(productPrice: ProductPublicModels.PublicProductPrice): UnitPrice {
    return productPrice?.unitPrice;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function getPercentUseCase(
    mapPrices: ProductMapPrices,
    {
        configEnableTriplePrice, configTriplePricePercent, configShowPercent, configTypePercent,
    },
    discountsPercentages: ProductPublicModels.PublicDiscountsPercentages
): PricePercent {
    const price: number = mapPrices.price.value;
    const oldPrice: ProductPricesDetail = mapPrices.oldPrice;

    let percent = 0;
    let priceToCompare = oldPrice;
    let discountPercent: number = discountsPercentages?.oldPriceDiscount
        ? Number(discountsPercentages?.oldPriceDiscount?.replace(',', '.')) // Hay una configuración de back que puede provocar que nos llegue el descuento con coma en vez de con punto
        : null;

    if (configEnableTriplePrice) {
        if (configTriplePricePercent === PriceRoundingType.ORIGINAL) {
            priceToCompare = getPriceToCompareORIGINALUseCase(mapPrices);

            discountPercent = discountsPercentages?.originalPriceDiscount
                ? Number(discountsPercentages?.originalPriceDiscount)
                : null;
        }

        if (configTriplePricePercent === PriceRoundingType.CUT) {
            priceToCompare = getPriceToCompareCUTUseCase(mapPrices);
        }
    }

    if (price !== priceToCompare.value) {
        const intPercent = price / priceToCompare.value * 100;
        if (intPercent > 0 && intPercent < 100) {
            percent = intPercent;
        }
    }

    const percentRounded = roundPercentageUseCase(percent, configTypePercent, discountPercent);
    const value = percentRounded === 100 ? null : percentRounded;

    const showPercent = configShowPercent
        && (configTriplePricePercent === PriceRoundingType.ORIGINAL || configTriplePricePercent === PriceRoundingType.CUT)
        && !!value;

    return {
        value,
        placePrice: showPercent,
        showDisclaimer: configEnableTriplePrice && configTriplePricePercent === PriceRoundingType.ORIGINAL && value > 0,
    };
}

export function roundPercentageUseCase(
    percent: number,
    configTypePercent: string,
    discountPercent: number
): number {
    const unroundedPercentage = 100 - percent;

    switch (configTypePercent) {
        case '1': // Redondeado al entero más cercano
            return Math.floor(unroundedPercentage);
        case '2': // Descuento con decimales (tal y como nos viene en la API)
            if (discountPercent) {
                return discountPercent;
            }
            // En caso que nos llegue en la config el valor "2" y no nos llegue el objeto desde back, pintaremos por defecto el redondea al entero más cercano (1).
            return Math.floor(unroundedPercentage);
        default: // Redondeado al múltiplo de 5 más cercano
            return Math.floor(unroundedPercentage / 5) * 5;
    }
}

export function getPriceToCompareORIGINALUseCase(mapPrices: ProductMapPrices): ProductPricesDetail {
    if (mapPrices.originalPrice.value) {
        return mapPrices.originalPrice;
    }
    // CASE PREWARM/VEA
    // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar comprobación VIPPRICE del if)
    if (mapPrices.price.type === PricesType.FUTUREPRICE || mapPrices.price.type === PricesType.VIPPRICE) {
        if (mapPrices.lastReducedPrice.value) {
            return mapPrices.lastReducedPrice;
        }
        if (mapPrices.oldPrice.value) {
            return mapPrices.oldPrice;
        }
        return mapPrices.price;
    }
    return mapPrices.oldPrice;
}

export function getPriceToCompareCUTUseCase(mapPrices: ProductMapPrices): ProductPricesDetail {
    // CASE PREWARM
    // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar comprobación VIPPRICE del if)
    if (mapPrices.price.type === PricesType.FUTUREPRICE || mapPrices.price.type === PricesType.VIPPRICE) {
        if (mapPrices.lastReducedPrice.value && mapPrices.lastReducedPrice.value < mapPrices.oldPrice.value) {
            return mapPrices.lastReducedPrice;
        }
        if (mapPrices.oldPrice.value) {
            return mapPrices.oldPrice;
        }
        return mapPrices.price;
    }
    if (mapPrices.price.type === PricesType.PRICE) {
        if (mapPrices.oldPrice.value && mapPrices.oldPrice.value > mapPrices.price.value) {
            return mapPrices.oldPrice;
        }
        if (mapPrices.lastReducedPrice.value) {
            return mapPrices.lastReducedPrice;
        }
    }
    return mapPrices.oldPrice;
}

export function determinePeriodUser(period: string, showPrivateSale: boolean): string {
    if (period === SalesPeriod.VEA && !showPrivateSale) {
        return SalesPeriod.PREWARM;
    }
    return period;
}
